import { useEffect, useState, useMemo } from "react";
import { api, formatErr, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, MapPin, ClipboardCheck, CheckCircle2, XCircle, HandCoins } from "lucide-react";
import { toast } from "sonner";
import Attachments from "../components/Attachments";
import Comments from "../components/Comments";

const VISIT_TYPES = [
  { key: "site_survey", label: "Site Survey" },
  { key: "pre_installation", label: "Pre-Installation" },
  { key: "installation", label: "Installation" },
  { key: "service", label: "Service" },
  { key: "handover", label: "Handover" },
  { key: "troubleshooting", label: "Troubleshooting" },
];

const VISIT_STATUSES = [
  { key: "scheduled", label: "Scheduled", color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  { key: "in_progress", label: "In Progress", color: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  { key: "completed", label: "Completed", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  { key: "cancelled", label: "Cancelled", color: "bg-red-500/20 text-red-300 border-red-500/30" },
  { key: "no_show", label: "No Show", color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
];

const emptyVisit = {
  project_id: "", visit_type: "site_survey", visit_date: "",
  is_outstation: false, engineer_ids: [], helper_ids: [], helper_is_local: false,
  site_location: "", purpose: "", planned_start_time: "",
  status: "scheduled",
};

export default function SiteVisits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [visits, setVisits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ status: "", visit_type: "", engineer_id: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyVisit);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/site-visits", { params });
    setVisits(data);
  };

  useEffect(() => { load(); }, [filters]);
  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const engineers = useMemo(() => users.filter((u) =>
    (u.roles || [u.role]).some((r) => ["installation", "service", "engineer"].includes(r))
  ), [users]);

  const openNew = () => { setEditId(null); setForm(emptyVisit); setOpen(true); };
  const openEdit = (v) => {
    setEditId(v.id);
    setForm({
      project_id: v.project_id, visit_type: v.visit_type, visit_date: v.visit_date,
      is_outstation: !!v.is_outstation,
      engineer_ids: v.engineer_ids || [], helper_ids: v.helper_ids || [],
      helper_is_local: !!v.helper_is_local,
      site_location: v.site_location || "", purpose: v.purpose || "",
      planned_start_time: v.planned_start_time || "",
      status: v.status || "scheduled",
      work_completed: v.work_completed || "",
      pending_points: v.pending_points || "",
      materials_used: v.materials_used || "",
      issues_found: v.issues_found || "",
      engineer_remarks: v.engineer_remarks || "",
      client_sign_off_captured: !!v.client_sign_off_captured,
      client_sign_off_name: v.client_sign_off_name || "",
      manager_approved: !!v.manager_approved,
      next_visit_required: !!v.next_visit_required,
      _incentive: v.incentive,
    });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (!form.project_id || !form.visit_date) {
        toast.error("Project and visit date are required"); setBusy(false); return;
      }
      if (editId) {
        const { _incentive: _drop, ...payload } = form;
        void _drop;
        await api.patch(`/site-visits/${editId}`, payload);
        toast.success("Visit updated");
      } else {
        await api.post("/site-visits", form);
        toast.success("Visit created");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.client_name || "—";
  const userName = (id) => users.find((u) => u.id === id)?.name || "?";

  const totalEligibleThisMonth = visits.filter((v) => v.incentive?.eligible).reduce((s, v) => s + (v.incentive?.total_amount || 0), 0);
  const completedCount = visits.filter((v) => v.status === "completed").length;
  const pendingCount = visits.filter((v) => v.status === "scheduled" || v.status === "in_progress").length;

  const toggleId = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  return (
    <div className="space-y-4" data-testid="site-visits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Field Operations</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Site Visits</h1>
          <p className="text-xs text-muted-foreground mt-1">Survey · Installation · Service · Handover · Troubleshooting</p>
        </div>
        <Button onClick={openNew} data-testid="new-visit-btn"><Plus className="h-4 w-4 mr-2" />New Visit</Button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Scheduled / In-progress</div>
          <div className="font-mono text-2xl mt-0.5 text-primary">{pendingCount}</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</div>
          <div className="font-mono text-2xl mt-0.5 text-emerald-400">{completedCount}</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total visits</div>
          <div className="font-mono text-2xl mt-0.5">{visits.length}</div>
        </Card>
        <Card className="p-3 bg-card border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <HandCoins className="h-3 w-3" /> Eligible incentives
          </div>
          <div className="font-mono text-2xl mt-0.5 text-[hsl(var(--brand-yellow))]">{inr(totalEligibleThisMonth)}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-card border-border">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{VISIT_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.visit_type || "all"} onValueChange={(v) => setFilters({ ...filters, visit_type: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Visit type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{VISIT_TYPES.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.engineer_id || "all"} onValueChange={(v) => setFilters({ ...filters, engineer_id: v === "all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Engineer" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All engineers</SelectItem>{engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Engineer(s)</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Incentive</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visits.map((v) => {
              const st = VISIT_STATUSES.find((s) => s.key === v.status);
              const inc = v.incentive || {};
              return (
                <TableRow key={v.id} className="cursor-pointer" onClick={() => openEdit(v)} data-testid={`visit-row-${v.id}`}>
                  <TableCell className="font-mono text-xs">{v.visit_date}</TableCell>
                  <TableCell className="font-medium">{projectName(v.project_id)}</TableCell>
                  <TableCell className="capitalize text-xs">{v.visit_type?.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-xs">
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {v.site_location || "—"}
                      {v.is_outstation && <Badge variant="outline" className="text-[10px] ml-1">Outstation</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">
                    {(v.engineer_ids || []).map((e) => userName(e)).join(", ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${st?.color} border`}>{st?.label}</Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono">
                    {inc.eligible ? (
                      <span className="text-emerald-400">{inr(inc.total_amount)} ✓</span>
                    ) : (
                      <span className="text-muted-foreground" title={inc.reasons?.join(", ")}>
                        {inr(inc.per_engineer_amount || 0)} · pending
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {visits.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No visits found. Create one to get started.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Visit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="visit-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit Site Visit" : "New Site Visit"}</DialogTitle>
          </DialogHeader>

          {editId && form._incentive && (
            <div className="rounded border border-border bg-secondary/30 p-3 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="uppercase tracking-wider text-muted-foreground">Incentive</span>
                {form._incentive.eligible
                  ? <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 border"><CheckCircle2 className="h-3 w-3 mr-1" />Eligible · {inr(form._incentive.total_amount)}</Badge>
                  : <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 border"><XCircle className="h-3 w-3 mr-1" />Pending · slab {inr(form._incentive.per_engineer_amount)}</Badge>}
              </div>
              {form._incentive.reasons?.length > 0 && (
                <div className="text-muted-foreground">Blockers: {form._incentive.reasons.join(" · ")}</div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Project" required>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="visit-project"><SelectValue placeholder="Choose project" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Visit Type" required>
              <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                <SelectTrigger data-testid="visit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{VISIT_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Visit Date" required>
              <Input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="visit-date" />
            </Field>
            <Field label="Planned Start Time">
              <Input type="time" value={form.planned_start_time || ""} onChange={(e) => setForm({ ...form, planned_start_time: e.target.value })} />
            </Field>
            <Field label="Site Location">
              <Input placeholder="Address / area" value={form.site_location || ""} onChange={(e) => setForm({ ...form, site_location: e.target.value })} />
            </Field>
            <Field label="Local / Outstation">
              <label className="flex items-center gap-2 h-10">
                <input type="checkbox" checked={!!form.is_outstation} onChange={(e) => setForm({ ...form, is_outstation: e.target.checked })} data-testid="visit-outstation" />
                <span className="text-sm">Outstation visit</span>
              </label>
            </Field>
            <Field label="Engineers Assigned" hint="Multi-select — used for incentive slab">
              <div className="space-y-1 border border-border rounded p-2 bg-secondary/20 max-h-32 overflow-y-auto">
                {engineers.length === 0 && <div className="text-xs text-muted-foreground">No engineers found. Assign roles: installation / service.</div>}
                {engineers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={form.engineer_ids.includes(u.id)}
                           onChange={() => setForm({ ...form, engineer_ids: toggleId(form.engineer_ids, u.id) })} />
                    {u.name}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Helpers (optional)" hint="Untrained helpers assisting the engineer">
              <div className="space-y-1 border border-border rounded p-2 bg-secondary/20 max-h-32 overflow-y-auto">
                {users.filter((u) => !engineers.some((e) => e.id === u.id)).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" checked={form.helper_ids.includes(u.id)}
                           onChange={() => setForm({ ...form, helper_ids: toggleId(form.helper_ids, u.id) })} />
                    {u.name}
                  </label>
                ))}
                <label className="flex items-center gap-2 cursor-pointer text-xs pt-1 border-t border-border mt-1">
                  <input type="checkbox" checked={!!form.helper_is_local}
                         onChange={(e) => setForm({ ...form, helper_is_local: e.target.checked })} />
                  Helper is <em>local</em> at outstation site (activates ₹1,500 slab)
                </label>
              </div>
            </Field>
            <div className="col-span-2">
              <Field label="Purpose">
                <Textarea rows={2} value={form.purpose || ""} onChange={(e) => setForm({ ...form, purpose: e.target.value })} />
              </Field>
            </div>

            {editId && (
              <>
                <Field label="Status">
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="visit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{VISIT_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Next Visit Required">
                  <label className="flex items-center gap-2 h-10">
                    <input type="checkbox" checked={!!form.next_visit_required} onChange={(e) => setForm({ ...form, next_visit_required: e.target.checked })} />
                    <span className="text-sm">Yes, follow-up needed</span>
                  </label>
                </Field>
                <div className="col-span-2">
                  <Field label="Work Completed" hint="Required for incentive payout">
                    <Textarea rows={2} value={form.work_completed || ""} onChange={(e) => setForm({ ...form, work_completed: e.target.value })} data-testid="visit-work" />
                  </Field>
                </div>
                <Field label="Pending Points"><Textarea rows={2} value={form.pending_points || ""} onChange={(e) => setForm({ ...form, pending_points: e.target.value })} /></Field>
                <Field label="Materials Used"><Textarea rows={2} value={form.materials_used || ""} onChange={(e) => setForm({ ...form, materials_used: e.target.value })} /></Field>
                <div className="col-span-2">
                  <Field label="Issues Found"><Textarea rows={2} value={form.issues_found || ""} onChange={(e) => setForm({ ...form, issues_found: e.target.value })} /></Field>
                </div>
                <div className="col-span-2">
                  <Field label="Engineer Remarks"><Textarea rows={2} value={form.engineer_remarks || ""} onChange={(e) => setForm({ ...form, engineer_remarks: e.target.value })} /></Field>
                </div>
                <Field label="Client Sign-off">
                  <label className="flex items-center gap-2 h-10">
                    <input type="checkbox" checked={!!form.client_sign_off_captured} onChange={(e) => setForm({ ...form, client_sign_off_captured: e.target.checked })} data-testid="visit-signoff" />
                    <span className="text-sm">Captured</span>
                  </label>
                </Field>
                <Field label="Client / Site Manager Name">
                  <Input value={form.client_sign_off_name || ""} onChange={(e) => setForm({ ...form, client_sign_off_name: e.target.value })} />
                </Field>
                {isAdmin && (
                  <div className="col-span-2">
                    <Field label="Manager Approval (Admin only)" hint="Approve after reviewing work, sign-off, and photos — releases incentive">
                      <label className="flex items-center gap-2 h-10">
                        <input type="checkbox" checked={!!form.manager_approved} onChange={(e) => setForm({ ...form, manager_approved: e.target.checked })} data-testid="visit-approve" />
                        <span className="text-sm flex items-center gap-1"><ClipboardCheck className="h-4 w-4 text-primary" />Approve for incentive payout</span>
                      </label>
                    </Field>
                  </div>
                )}
              </>
            )}
          </div>

          {editId && (
            <div className="pt-3 border-t border-border space-y-4">
              <Attachments kind="site_visit" parentId={editId} />
              <Comments entity="site_visit" parentId={editId} />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="visit-save-btn">
              {busy ? "Saving…" : editId ? "Save" : "Create"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
